import React, { FC } from "react";

import {
  createFolderBusinessFilter,
  currentFolderRootBreadcrumbContext,
  DataViewKey,
  KpkMedia,
  MediaField,
  useFilterReset,
} from "@keepeek/commons";
import {
  BaseComponentProps,
  DisplayMode,
  FormatTechnicalFields,
} from "@keepeek/refront-components";
import * as Sentry from "@sentry/nextjs";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { backContextState } from "../../../providers/backContext/atoms";
import { KIcon } from "../../common/KIcon";

type MediaDetailHeaderTechnicalInfoProps = {
  element: KpkMedia;
  headerTechnicalFields: Array<string>;
} & BaseComponentProps;

const MediaDetailHeaderTechnicalInfo: FC<
  React.PropsWithChildren<MediaDetailHeaderTechnicalInfoProps>
> = function ({ element, headerTechnicalFields, className }) {
  const backContext = useRecoilValue(backContextState);
  const setCurrentFolderId = useSetRecoilState(currentFolderRootBreadcrumbContext);
  const dataViewKey = backContext.key || DataViewKey.SEARCH;
  const resetFilters = useFilterReset(dataViewKey, ({ category, message }) =>
    Sentry.addBreadcrumb({ category, message }),
  );
  const { t } = useTranslation();

  // Check if we will print at least one field
  let hasAtLeastOneField = element.fields
    // remove ones with value of -1
    ?.filter((f) => f.value !== "-1")
    // verify if we ask for it
    .some((f) => headerTechnicalFields.includes(f.id));
  hasAtLeastOneField =
    hasAtLeastOneField ||
    (headerTechnicalFields.includes(MediaField.FOLDER_ID) &&
      element.folders != undefined &&
      element.folders.length > 0);
  if (!hasAtLeastOneField) {
    return null;
  }

  return (
    <FormatTechnicalFields
      className={clsx(className, "MediaDetailHeaderTechnicalInfo")}
      element={element}
      technicalFieldsToDisplay={headerTechnicalFields}
      onFolderClick={(folder) => {
        setCurrentFolderId(undefined); // Remove current folderId to get root from hierarchy
        const businessFilter = createFolderBusinessFilter(
          folder.id,
          folder?.title ?? "",
          t("business-filter.advanced.folder.label"),
        );

        resetFilters({
          exceptFolderFilter: false,
          exceptBasketFilter: dataViewKey === DataViewKey.BASKET_DETAIL,
          filterToAddAfterReset: businessFilter,
        });
      }}
      folderDisplayMode={
        backContext.key === DataViewKey.BASKET_DETAIL
          ? DisplayMode.ALL_FOLDERS_WITHOUT_LINKS
          : DisplayMode.ALL_FOLDERS_WITH_LINKS
      }
      KIcon={KIcon}
    />
  );
};

export default MediaDetailHeaderTechnicalInfo;
